import { observable, when } from 'mobx';
import {
  applySnapshot,
  cast,
  flow,
  getRoot,
  getSnapshot,
  isAlive,
  types,
} from 'mobx-state-tree';
import { categorizeFileType, isAbortError } from 'utils';
import {
  buildChatsFilterParams,
  buildMessagePreview,
  buildSenderName,
  chatMapKey,
  isChatConnected,
  isCurrentUserSender,
  normalizeMassiveMessage,
  parseChatId,
  uploadAttachmentFile,
} from './utils';
import {
  HouseChatAttachmentTabType,
  HouseChatsStatusFilter,
  IHouseChatSidebarItem,
  IMessageSearchResultItem,
} from './types';
import { GW_SERVICES_PREFIXES } from 'constants/gateway';
import {
  ATTACHMENT_RESOLVE_CONCURRENCY,
  ATTACHMENTS_TAB_PAGE_SIZE,
  CHAT_STATUS_ORDER,
  CHATS_PAGE_SIZE,
  HISTORY_CACHE_LIMIT,
  HISTORY_PAGE_SIZE,
  HISTORY_UNREAD_MAX_LOAD,
  IMAGE_ATTACHMENT_TYPES,
  MAX_INITIAL_LOAD_ATTEMPTS,
  STATUS_FILTERS,
  THUMBNAIL_CONCURRENCY,
  UNREAD_CONTEXT,
} from './constants';
import { HouseChats } from 'http-requests';
import { ChatItemDTO, MessageSearchItemDTO } from 'http-requests/house-chats';
import {
  AttachmentCacheEntryModel,
  attachmentCacheKey,
  attachmentsTabKey,
  ChatAttachmentsTabModel,
  ChatDraftModel,
  ChatEmployeesModel,
  ChatHistoryModel,
  ChatItemModel,
  ChatParticipantsModel,
  ChatStatsModel,
  RequestsModel,
  TAttachmentCacheEntry,
  TChatAttachmentsTab,
  TChatEmployees,
  TChatHistory,
  TChatItem,
  TChatParticipants,
  TChatStats,
  TSystemMessageEntity,
} from './models';
import { UIStates } from './models/ui-states';
import {
  MaxBotAddedPayload,
  MaxBotRemovedPayload,
  MaxMessageCreatedPayload,
  MaxMessageEditedPayload,
  MaxMessageRemovedPayload,
  MaxUserAddedPayload,
  MaxUserRemovedPayload,
} from 'socket/events/max-chats';
import { message } from 'components/ui';

import {
  collectAttachmentTabTypes,
  normalizeAttachments,
  normalizeMaxMessage,
} from './max-events';
import { createImageThumbnail } from './thumbnail';

const SYSTEM_ENTITIES_FLUSH_DELAY_MS = 50;
// Зеркало лимита ручки доступности в s300-dispatcher (SYSTEM_ENTITIES_MAX
// в api/routers/max_chat/schemas.py, отдельно на заявки и обращения):
// больше в одном запросе - 422. Пачка общая на оба типа, поэтому ни один
// список её не превысит. Менять вместе с бэкендом.
const SYSTEM_ENTITIES_BATCH_SIZE = 200;

const systemEntityKey = ({
  type,
  id,
}: Pick<TSystemMessageEntity, 'type' | 'id'>): string => `${type}:${id}`;

export const HouseChatsStore = types
  .model('HouseChatsStore', {
    requestsStates: types.optional(RequestsModel, {}),
    draftsByChatId: types.optional(types.map(ChatDraftModel), {}),
    chatsByChatId: types.optional(types.map(ChatItemModel), {}),
    addressMatchChats: types.optional(types.array(ChatItemModel), []),
    selectedId: types.optional(types.maybeNull(types.string), null),
    activeChatId: types.optional(types.maybeNull(types.number), null),
    historyByChatId: types.optional(types.map(ChatHistoryModel), {}),
    recentChatOrder: types.optional(types.array(types.string), []),
    attachmentCache: types.optional(types.map(AttachmentCacheEntryModel), {}),
    participantsByChatId: types.optional(types.map(ChatParticipantsModel), {}),
    employeesByChatId: types.optional(types.map(ChatEmployeesModel), {}),
    attachmentsByTabKey: types.optional(types.map(ChatAttachmentsTabModel), {}),
    statsByChatId: types.optional(types.map(ChatStatsModel), {}),
    massMailingHistory: types.optional(ChatHistoryModel, {}),
    uiStates: types.optional(UIStates, {}),
    searchQuery: types.optional(types.string, ''),
    initialLoadAttempts: types.optional(types.number, 0),
    statusFilter: types.optional(
      types.enumeration<HouseChatsStatusFilter>(
        'HouseChatsStatusFilter',
        STATUS_FILTERS
      ),
      'all'
    ),
    unreadOnly: types.optional(types.boolean, false),
  })
  .volatile(() => ({
    pendingFilesByTempId: new Map<string, File[]>(),
    // Кэш результатов загрузки по tempId — если ForwardMessage упал уже
    // после успешной заливки файлов, retrySend/retryMassSend не должны
    // заливать их заново, файл на files-микросервисе уже есть.
    uploadedFilesByTempId: new Map<
      string,
      { file_id: string; filename: string }[]
    >(),
    attachmentAborters: new Map<string, AbortController>(),
    attachmentQueue: [] as {
      chatId: number;
      messageId: string;
      attachmentId: string;
    }[],
    tabAborters: new Map<string, AbortController>(),
    // чтобы серия сообщений в один и тот же неизвестный чат не разошлась
    // серией одинаковых запросов (см. pullChatIntoList)
    pullingChatIds: new Set<number>(),
    massMailingStaged: [] as { id: string; text: string; files: File[] }[],
    chatsOffset: 0,
    chatsTotal: null as number | null,
    // plain Set не годится: MST не оборачивает его в ObservableSet при
    // попадании в volatile, .add()/.delete() молча не триггерят реакции
    refreshingHouseIds: observable.set<string>(),
    messageSearchResults: [] as MessageSearchItemDTO[],
    searchAborter: null as AbortController | null,
    // общий на все чтения раздела: обрывается разом в resetSection, чтобы
    // ответ по дороге не наполнил уже очищенный стор
    sectionAborter: new AbortController(),
    // для точки у пункта меню — считается отдельным запросом, а не по
    // chatsByChatId: там лежит только текущая отфильтрованная страница
    hasUnreadChats: false,
    unreadCheckPending: false,
    addressMatchesTruncated: false,
    messageSearchOffset: 0,
    messageSearchTotal: null as number | null,
    // Можно ли текущему сотруднику открыть заявку/обращение из системного
    // сообщения: ключ systemEntityKey, нет ключа - ещё не
    // проверяли. Признак персональный, в payload сообщения его нет (WS
    // рассылает одно сообщение всем) - спрашиваем отдельно, пачкой.
    systemEntityAvailability: observable.map<string, boolean>(),
    // ждут отправки или уже в запросе - чтобы не спрашивать повторно
    requestedSystemEntities: new Map<
      string,
      Pick<TSystemMessageEntity, 'type' | 'id'>
    >(),
    queuedSystemEntityKeys: new Set<string>(),
    systemEntitiesFlushTimer: null as ReturnType<typeof setTimeout> | null,
  }))
  .views((self) => ({
    get currentUserNumber(): string {
      return getRoot<{ user: { auth: { number: string } } }>(self).user.auth
        .number;
    },
    isSystemEntityAvailable(
      entity: Pick<TSystemMessageEntity, 'type' | 'id'>
    ): boolean | undefined {
      return self.systemEntityAvailability.get(systemEntityKey(entity));
    },
    get hasMoreChats(): boolean {
      return self.chatsTotal === null || self.chatsOffset < self.chatsTotal;
    },
    get hasMoreMessageSearchResults(): boolean {
      return (
        self.messageSearchTotal === null ||
        self.messageSearchOffset < self.messageSearchTotal
      );
    },
  }))
  .views((self) => ({
    buildSidebarItem(
      chat: TChatItem,
      addressByHouseId: Map<string, string>
    ): IHouseChatSidebarItem {
      const houseAddresses = chat.house_ids
        .map((houseId) => addressByHouseId.get(houseId))
        .filter((address): address is string => !!address);
      const address = houseAddresses.join('; ');
      const title = chat.chat_title || address || 'Без названия';
      const houseId = chat.house_ids[0] ?? null;

      const draftEntry =
        chat.chat_id === self.activeChatId
          ? undefined
          : self.draftsByChatId.get(String(chat.chat_id));
      const draftText = draftEntry
        ? draftEntry.text.trim() ||
          (draftEntry.reply ? draftEntry.reply.preview : '')
        : '';

      // Если бот лишился прав администратора уже ПОСЛЕ того, как история
      // синхронизировалась (last_message есть), показывать во весь экран
      // "как назначить админом" некуда — сообщения уже видны. Вместо этого
      // чат остаётся подключённым, но с баннером ошибки синхронизации
      // (syncError), как уже подключённый чат, а не как отключённый.
      const botNotAdminWithHistory =
        chat.status === 'bot_not_admin' && !!chat.last_message;

      const maxChatUrl = chat.max_chat_url || null;
      const connected = isChatConnected(chat);
      const botNotAdmin =
        chat.status === 'bot_not_admin' && !botNotAdminWithHistory;
      const refreshing = chat.house_ids.some((id) =>
        self.refreshingHouseIds.has(id)
      );
      const needsChatLink = !connected && !botNotAdmin && !maxChatUrl;

      return {
        id: `chat-${chatMapKey(chat)}`,
        chatId: chat.chat_id,
        status: chat.status,
        title,
        titleIsAddress: !chat.chat_title,
        address,
        houseAddresses,
        houseId,
        maxChatUrl,
        connected,
        botNotAdmin,
        needsChatLink,
        syncError: botNotAdminWithHistory || chat.status === 'disconnected',
        refreshing,
        hasInfoColumn: !refreshing,
        lastMessage: chat.last_message
          ? {
              text: chat.last_message.text,
              createdAt: chat.last_message.created_at,
              senderName: chat.senderName,
            }
          : null,
        preview: chat.preview,
        draft: draftText || null,
        unreadCount: chat.unread_count,
      };
    },
  }))
  .views((self) => ({
    getAllItems(
      houses: { house_id: string; address: string }[]
    ): IHouseChatSidebarItem[] {
      const addressByHouseId = new Map(
        houses.map((house) => [house.house_id, house.address])
      );

      const chatRows: IHouseChatSidebarItem[] = [];

      self.chatsByChatId.forEach((chat) => {
        chatRows.push(self.buildSidebarItem(chat, addressByHouseId));
      });

      return chatRows;
    },
    getAddressMatchItems(
      houses: { house_id: string; address: string }[]
    ): IHouseChatSidebarItem[] {
      const addressByHouseId = new Map(
        houses.map((house) => [house.house_id, house.address])
      );

      return self.addressMatchChats.map((chat) =>
        self.buildSidebarItem(chat, addressByHouseId)
      );
    },
  }))
  .views((self) => ({
    getSidebarItems(
      houses: { house_id: string; address: string }[]
    ): IHouseChatSidebarItem[] {
      const items = self
        .getAllItems(houses)
        .filter(
          (item) =>
            !self.unreadOnly ||
            !!item.unreadCount ||
            item.chatId === self.activeChatId
        );

      return items.slice().sort((a, b) => {
        const statusDiff =
          CHAT_STATUS_ORDER.indexOf(a.status) -
          CHAT_STATUS_ORDER.indexOf(b.status);

        if (statusDiff !== 0) {
          return statusDiff;
        }

        // bot_not_added: чаты с уже привязанной ссылкой на MAX — впереди тех,
        // где ссылки ещё нет вовсе
        if (a.status === 'bot_not_added') {
          const linkDiff = Number(!!b.maxChatUrl) - Number(!!a.maxChatUrl);

          if (linkDiff !== 0) {
            return linkDiff;
          }
        }

        const aTime = a.lastMessage?.createdAt
          ? new Date(a.lastMessage.createdAt).getTime()
          : 0;
        const bTime = b.lastMessage?.createdAt
          ? new Date(b.lastMessage.createdAt).getTime()
          : 0;

        return bTime - aTime;
      });
    },
  }))
  .views((self) => ({
    getMessageSearchItems(): IMessageSearchResultItem[] {
      return self.messageSearchResults.map((item) => ({
        id: `${item.chat_id}-${item.max_message_id}`,
        chatId: item.chat_id,
        chatTitle: item.chat_title || 'Без названия',
        maxMessageId: item.max_message_id,
        createdAt: item.created_at,
        senderName:
          buildSenderName(item.system_sender) ?? buildSenderName(item.sender),
        preview: buildMessagePreview(item),
      }));
    },
  }))
  .views((self) => ({
    getSelectedItem(
      houses: { house_id: string; address: string }[]
    ): IHouseChatSidebarItem | null {
      if (!self.selectedId) {
        return null;
      }

      return (
        self.getAllItems(houses).find((item) => item.id === self.selectedId) ??
        null
      );
    },
    get hasConnectedChats(): boolean {
      return Array.from(self.chatsByChatId.values()).some(
        (chat) => chat.status === 'connected'
      );
    },
    // Чат доступен для полноценного взаимодействия (ответить/редактировать/
    // удалить) только пока бот подключён и админ — 'disconnected' и
    // 'bot_not_admin' (с уже пришедшей историей, см. getSidebarItems) на
    // ConnectedChat всё ещё показывают сообщения, но только для чтения.
    isChatReadOnly(chatId: number): boolean {
      return self.chatsByChatId.get(String(chatId))?.status !== 'connected';
    },
    get canLoadInitialData(): boolean {
      const { getChats } = self.requestsStates;

      if (getChats.pending || getChats.fulfilled) {
        return false;
      }

      return self.initialLoadAttempts < MAX_INITIAL_LOAD_ATTEMPTS;
    },
    get initialLoadFailed(): boolean {
      const { getChats } = self.requestsStates;

      return (
        self.initialLoadAttempts >= MAX_INITIAL_LOAD_ATTEMPTS &&
        !getChats.pending &&
        getChats.error
      );
    },
    getHistory(chatId: number): TChatHistory | null {
      return self.historyByChatId.get(String(chatId)) ?? null;
    },
    getDraft(chatId: number) {
      return self.draftsByChatId.get(String(chatId)) ?? null;
    },
    getChatParticipants(chatId: number): TChatParticipants | null {
      return self.participantsByChatId.get(String(chatId)) ?? null;
    },
    getChatStats(chatId: number): TChatStats | null {
      return self.statsByChatId.get(String(chatId)) ?? null;
    },
    getChatEmployees(chatId: number): TChatEmployees | null {
      return self.employeesByChatId.get(String(chatId)) ?? null;
    },
    getAttachmentsTab(
      chatId: number,
      type: HouseChatAttachmentTabType
    ): TChatAttachmentsTab | null {
      return (
        self.attachmentsByTabKey.get(attachmentsTabKey(chatId, type)) ?? null
      );
    },
  }))
  .actions((self) => ({
    markChatRead: flow(function* (chatId: number, maxMessageId: string) {
      try {
        yield HouseChats.MarkRead({
          chat_id: chatId,
          max_message_id: maxMessageId,
        });

        self.chatsByChatId.get(String(chatId))?.markRead();
      } catch (error) {
        console.error(error);
      }
    }),
    abortChatRequests(chatId: number) {
      const prefix = `${chatId}:`;

      [self.tabAborters, self.attachmentAborters].forEach((aborters) => {
        aborters.forEach((controller, key) => {
          if (key.startsWith(prefix)) {
            controller.abort();
            aborters.delete(key);
          }
        });
      });

      self.attachmentQueue = self.attachmentQueue.filter(
        (task) => task.chatId !== chatId
      );
    },
    touchHistoryAccess(chatId: number) {
      const key = String(chatId);
      const existingIndex = self.recentChatOrder.indexOf(key);

      if (existingIndex !== -1) {
        self.recentChatOrder.splice(existingIndex, 1);
      }

      self.recentChatOrder.push(key);

      while (self.recentChatOrder.length > HISTORY_CACHE_LIMIT) {
        const evictKey = self.recentChatOrder[0];

        if (evictKey === key) {
          break;
        }

        self.recentChatOrder.shift();
        self.historyByChatId.delete(evictKey);
      }
    },
    getOrCreateHistory(chatId: number): TChatHistory {
      const key = String(chatId);
      let history = self.historyByChatId.get(key);

      if (!history) {
        self.historyByChatId.set(key, {});
        history = self.historyByChatId.get(key)!;
        history.setChatId(chatId);
      }

      return history;
    },
    setDraftText(chatId: number, text: string) {
      const key = String(chatId);
      const draft = self.draftsByChatId.get(key);

      if (draft) {
        draft.text = text;

        if (!draft.text && !draft.reply && !draft.editing) {
          self.draftsByChatId.delete(key);
        }
      } else if (text) {
        self.draftsByChatId.set(key, { text });
      }
    },
    startEditMessage(chatId: number, maxMessageId: string, text: string) {
      self.draftsByChatId.set(String(chatId), {
        text,
        reply: null,
        editing: { maxMessageId, preview: text || 'Вложение' },
      });
    },
    setDraftReply(
      chatId: number,
      reply: {
        maxMessageId: string;
        senderName: string;
        preview: string;
      } | null
    ) {
      const key = String(chatId);
      const draft = self.draftsByChatId.get(key);

      if (reply) {
        self.draftsByChatId.set(key, {
          text: draft?.editing ? '' : (draft?.text ?? ''),
          reply,
          editing: null,
        });

        return;
      }

      if (!draft) {
        return;
      }

      if (!draft.text) {
        self.draftsByChatId.delete(key);

        return;
      }

      self.draftsByChatId.set(key, {
        text: draft.text,
        reply: null,
        editing: null,
      });
    },
    clearDraft(chatId: number) {
      self.draftsByChatId.delete(String(chatId));
    },
    getOrCreateParticipants(chatId: number): TChatParticipants {
      const key = String(chatId);

      if (!self.participantsByChatId.has(key)) {
        self.participantsByChatId.set(key, {});
      }

      return self.participantsByChatId.get(key)!;
    },
    getOrCreateEmployees(chatId: number): TChatEmployees {
      const key = String(chatId);

      if (!self.employeesByChatId.has(key)) {
        self.employeesByChatId.set(key, {});
      }

      return self.employeesByChatId.get(key)!;
    },
    getOrCreateAttachmentsTab(
      chatId: number,
      type: HouseChatAttachmentTabType
    ): TChatAttachmentsTab {
      const key = attachmentsTabKey(chatId, type);

      if (!self.attachmentsByTabKey.has(key)) {
        self.attachmentsByTabKey.set(key, {});
      }

      return self.attachmentsByTabKey.get(key)!;
    },
    getOrCreateChatStats(chatId: number): TChatStats {
      const key = String(chatId);

      if (!self.statsByChatId.has(key)) {
        self.statsByChatId.set(key, {});
      }

      return self.statsByChatId.get(key)!;
    },
    syncSidebarPreview(chatId: number) {
      const history = self.getHistory(chatId);
      const newest = history?.newestMessage;

      if (!history || history.hasNewer || !newest) {
        return;
      }

      const chatItem = self.chatsByChatId.get(String(chatId));

      if (!chatItem) {
        return;
      }

      const currentTime = chatItem.last_message?.created_at
        ? new Date(chatItem.last_message.created_at).getTime()
        : 0;

      if (new Date(newest.created_at).getTime() > currentTime) {
        chatItem.setLastMessage(getSnapshot(newest));
      }
    },
  }))
  // resolveAttachment/drainAttachmentQueue/runAttachmentFetch взаимно
  // рекурсивны (drain запускает fetch, fetch по завершении снова зовёт
  // drain) — self./this. между экшенами одного стора работают только в
  // одну сторону (self. видит лишь строго более ранние .actions()-блоки,
  // this. — только соседей по тому же блоку, и то не из flow), поэтому для
  // цикла между plain- и flow-методом такой связки в принципе не существует.
  // Обходим через обычные замыкания вместо self./this.
  .actions((self) => {
    function resolveAttachment(
      chatId: number,
      messageId: string,
      attachmentId: string
    ) {
      const key = attachmentCacheKey(chatId, messageId, attachmentId);
      const existing = self.attachmentCache.get(key);

      // 'loading' здесь покрывает и уже запущенные запросы, и уже
      // поставленные в очередь (см. drainAttachmentQueue) — повторный вызов
      // для того же ключа просто ничего не делает.
      if (existing && existing.status !== 'error') {
        return;
      }

      self.attachmentCache.set(key, { status: 'loading' });
      self.attachmentQueue.push({ chatId, messageId, attachmentId });
      drainAttachmentQueue();
    }

    /** Аналог resolveAttachment, но возвращает промис с итоговой записью кэша —
     * нужен там, где вложение переиспользуется не в UI чата, а в другом
     * потоке (например, перенос вложения сообщения в новую заявку), и
     * важно получить именно готовый результат: либо сразу из кэша (если
     * вложение уже резолвилось), либо дождавшись фактического запроса. */
    function resolveAttachmentAsync(
      chatId: number,
      messageId: string,
      attachmentId: string
    ): Promise<TAttachmentCacheEntry | undefined> {
      const key = attachmentCacheKey(chatId, messageId, attachmentId);
      const existing = self.attachmentCache.get(key);

      if (existing && existing.status !== 'loading') {
        return Promise.resolve(existing);
      }

      resolveAttachment(chatId, messageId, attachmentId);

      return new Promise((resolve) => {
        when(
          () => self.attachmentCache.get(key)?.status !== 'loading',
          () => resolve(self.attachmentCache.get(key))
        );
      });
    }

    function cancelAttachment(
      chatId: number,
      messageId: string,
      attachmentId: string
    ) {
      const key = attachmentCacheKey(chatId, messageId, attachmentId);
      const controller = self.attachmentAborters.get(key);

      if (controller) {
        // Дальнейшая очистка (aborters/cache) произойдёт в catch-ветке
        // runAttachmentFetch по isAbortError.
        controller.abort();

        return;
      }

      const wasQueued = self.attachmentQueue.some(
        (task) =>
          attachmentCacheKey(task.chatId, task.messageId, task.attachmentId) ===
          key
      );

      if (wasQueued) {
        self.attachmentQueue = self.attachmentQueue.filter(
          (task) =>
            attachmentCacheKey(
              task.chatId,
              task.messageId,
              task.attachmentId
            ) !== key
        );
        self.attachmentCache.delete(key);
      }
    }

    function drainAttachmentQueue() {
      while (
        self.attachmentQueue.length > 0 &&
        self.attachmentAborters.size < ATTACHMENT_RESOLVE_CONCURRENCY
      ) {
        const task = self.attachmentQueue.shift();

        if (task) {
          runAttachmentFetch(task.chatId, task.messageId, task.attachmentId);
        }
      }
    }

    // Отдельная от attachmentQueue очередь — та ограничивает сетевые
    // /get_attachment, эта пул decode+resize (CPU, не сеть), у них разная
    // природа затрат и не должны делить один лимит
    const thumbnailQueue: { key: string; url: string }[] = [];
    let thumbnailInFlight = 0;

    function queueThumbnail(key: string, url: string) {
      thumbnailQueue.push({ key, url });
      drainThumbnailQueue();
    }

    function drainThumbnailQueue() {
      while (
        thumbnailQueue.length > 0 &&
        thumbnailInFlight < THUMBNAIL_CONCURRENCY
      ) {
        const task = thumbnailQueue.shift();

        if (task) {
          runThumbnailGeneration(task.key, task.url);
        }
      }
    }

    const runThumbnailGeneration = flow(function* (key: string, url: string) {
      thumbnailInFlight += 1;

      try {
        const thumbUrl: string = yield createImageThumbnail(url);
        const entry = self.attachmentCache.get(key);

        // Запись могла уйти из кэша (ретрай/очистка) пока миниатюра
        // декодировалась — тогда применять thumbUrl уже некуда
        if (entry) {
          self.attachmentCache.set(key, { ...getSnapshot(entry), thumbUrl });
        }
      } catch (error) {
        // CORS/сетевые сбои декода не должны ронять остальной резолв.
        // thumbUrl всё равно выставляем (в оригинал) — компоненты ждут
        // именно thumbUrl перед показом img, без этого превью зависло бы в
        // skeleton навсегда
        console.error(error);

        const entry = self.attachmentCache.get(key);

        if (entry) {
          self.attachmentCache.set(key, {
            ...getSnapshot(entry),
            thumbUrl: url,
          });
        }
      } finally {
        thumbnailInFlight -= 1;
        drainThumbnailQueue();
      }
    });

    const runAttachmentFetch = flow(function* (
      chatId: number,
      messageId: string,
      attachmentId: string
    ) {
      const key = attachmentCacheKey(chatId, messageId, attachmentId);
      const controller = new AbortController();
      self.attachmentAborters.set(key, controller);

      try {
        const response = yield HouseChats.GetAttachment(
          {
            chat_id: chatId,
            message_id: messageId,
            attachment_id: Number(attachmentId),
          },
          controller.signal
        );

        const url =
          response.found && response.shared_id
            ? `${GW_SERVICES_PREFIXES.FILES_URL}/${response.shared_id}/`
            : null;

        self.attachmentCache.set(key, {
          url,
          size: response.found ? response.size : null,
          filename: response.found ? response.filename : null,
          status: url ? 'loaded' : 'error',
        });

        if (
          url &&
          IMAGE_ATTACHMENT_TYPES.includes((response.type ?? '').toLowerCase())
        ) {
          queueThumbnail(key, url);
        }
      } catch (error) {
        if (isAbortError(error)) {
          // Отменённый запрос не должен блокировать повторную попытку —
          // без сброса кэша запись осталась бы в 'loading' навсегда.
          self.attachmentCache.delete(key);
        } else {
          console.error(error);
          self.attachmentCache.set(key, { status: 'error' });
        }
      } finally {
        if (self.attachmentAborters.get(key) === controller) {
          self.attachmentAborters.delete(key);
        }

        drainAttachmentQueue();
      }
    });

    return {
      resolveAttachment,
      resolveAttachmentAsync,
      cancelAttachment,
      drainAttachmentQueue,
      runAttachmentFetch,
    };
  })
  .actions((self) => ({
    loadParticipants: flow(function* (chatId: number) {
      const entry = self.getOrCreateParticipants(chatId);

      if (entry.requestState.pending) {
        return;
      }

      if (entry.requestState.fulfilled && !entry.requestState.error) {
        return;
      }

      try {
        entry.requestState.loadingStates();

        const items = yield HouseChats.GetParticipants(
          chatId,
          self.sectionAborter.signal
        );

        entry.setItems(items);
        entry.requestState.successStates();
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        console.error(error);
        entry.requestState.errorStates();
      } finally {
        entry.requestState.finallyStates();
      }
    }),
    loadEmployees: flow(function* (chatId: number) {
      const entry = self.getOrCreateEmployees(chatId);

      if (entry.requestState.pending) {
        return;
      }

      if (entry.requestState.fulfilled && !entry.requestState.error) {
        return;
      }

      try {
        entry.requestState.loadingStates();

        const items = yield HouseChats.GetChatEmployees(
          chatId,
          self.sectionAborter.signal
        );

        entry.setItems(items);
        entry.requestState.successStates();
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        console.error(error);
        entry.requestState.errorStates();
      } finally {
        entry.requestState.finallyStates();
      }
    }),
    loadChatStats: flow(function* (chatId: number) {
      const entry = self.getOrCreateChatStats(chatId);

      if (entry.requestState.pending) {
        return;
      }

      try {
        entry.requestState.loadingStates();

        const stats = yield HouseChats.GetChatStats(
          { chat_id: chatId },
          self.sectionAborter.signal
        );

        entry.setStats(stats);
        entry.requestState.successStates();
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        console.error(error);
        entry.requestState.errorStates();
      } finally {
        entry.requestState.finallyStates();
      }
    }),
    // Бэк не шлёт статистику по сокету — если панель статистики этого чата уже
    // открывали (есть запись в statsByChatId) и это активный чат, досчитываем
    // счётчики повторным GET /chat_stats вместо инкремента на клиенте: не
    // знаем точных правил подсчёта на бэке (сообщения от бота/системы, ночная
    // граница "сегодня" и т.п.), поэтому не дублируем эту логику у себя.
    refreshChatStatsIfOpen(chatId: number) {
      if (
        self.activeChatId === chatId &&
        self.statsByChatId.has(String(chatId))
      ) {
        this.loadChatStats(chatId);
      }
    },
    loadAttachmentsTab: flow(function* (
      chatId: number,
      type: HouseChatAttachmentTabType
    ) {
      const tab = self.getOrCreateAttachmentsTab(chatId, type);

      if (tab.requestState.pending) {
        return;
      }

      if (tab.requestState.fulfilled && !tab.requestState.error) {
        return;
      }

      const key = attachmentsTabKey(chatId, type);

      self.tabAborters.get(key)?.abort();
      const controller = new AbortController();
      self.tabAborters.set(key, controller);

      try {
        tab.requestState.loadingStates();

        const response = yield HouseChats.GetChatAttachments(
          {
            chat_id: chatId,
            attachment_type: type,
            limit: ATTACHMENTS_TAB_PAGE_SIZE,
            offset: 0,
          },
          controller.signal
        );

        tab.setItems(response.items, response.total);
        tab.requestState.successStates();
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        console.error(error);
        tab.requestState.errorStates();
      } finally {
        if (self.tabAborters.get(key) === controller) {
          self.tabAborters.delete(key);
        }

        tab.requestState.finallyStates();
      }
    }),
    retryAttachmentsTab(chatId: number, type: HouseChatAttachmentTabType) {
      const key = attachmentsTabKey(chatId, type);

      self.tabAborters.get(key)?.abort();
      self.tabAborters.delete(key);
      self.attachmentsByTabKey.delete(key);
      this.loadAttachmentsTab(chatId, type);
    },
    loadMoreAttachmentsTab: flow(function* (
      chatId: number,
      type: HouseChatAttachmentTabType
    ) {
      const tab = self.getAttachmentsTab(chatId, type);

      if (!tab || !tab.hasMore || tab.loadMoreState.pending) {
        return;
      }

      const key = `${attachmentsTabKey(chatId, type)}:more`;

      self.tabAborters.get(key)?.abort();
      const controller = new AbortController();
      self.tabAborters.set(key, controller);

      try {
        tab.loadMoreState.loadingStates();

        const response = yield HouseChats.GetChatAttachments(
          {
            chat_id: chatId,
            attachment_type: type,
            limit: ATTACHMENTS_TAB_PAGE_SIZE,
            offset: tab.items.length,
          },
          controller.signal
        );

        tab.appendItems(response.items, response.total);
        tab.loadMoreState.successStates();
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        console.error(error);
        tab.loadMoreState.errorStates();
      } finally {
        if (self.tabAborters.get(key) === controller) {
          self.tabAborters.delete(key);
        }

        tab.loadMoreState.finallyStates();
      }
    }),
    loadLatest: flow(function* (chatId: number) {
      const history = self.getOrCreateHistory(chatId);
      const unread = self.chatsByChatId.get(String(chatId))?.unread_count ?? 0;

      const limit =
        unread > HISTORY_PAGE_SIZE
          ? Math.min(unread + UNREAD_CONTEXT, HISTORY_UNREAD_MAX_LOAD)
          : HISTORY_PAGE_SIZE;

      try {
        history.requestsState.loadingStates();

        const response = yield HouseChats.GetHistory(
          {
            chat_id: chatId,
            limit,
          },
          self.sectionAborter.signal
        );

        history.setLatest(response.items, response.total);
        history.setUnreadBoundary(unread);
        self.syncSidebarPreview(chatId);
        history.requestsState.successStates();

        const newestId = history.newestMessage?.max_message_id;

        if (
          newestId &&
          self.activeChatId === chatId &&
          self.uiStates.activeChatAtBottom
        ) {
          self.markChatRead(chatId, newestId);
        }
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        console.error(error);
        history.requestsState.errorStates();
      } finally {
        history.requestsState.finallyStates();
      }
    }),
    refreshLatestTail: flow(function* (chatId: number) {
      const history = self.getHistory(chatId);

      if (!history) {
        return;
      }

      try {
        history.backgroundRefreshState.loadingStates();

        const response = yield HouseChats.GetHistory(
          {
            chat_id: chatId,
            limit: HISTORY_PAGE_SIZE,
          },
          self.sectionAborter.signal
        );

        history.mergeLatest(response.items, response.total);
        self.syncSidebarPreview(chatId);
        history.backgroundRefreshState.successStates();

        const newestId = history.newestMessage?.max_message_id;

        if (newestId && self.uiStates.activeChatAtBottom) {
          self.markChatRead(chatId, newestId);
        }
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        console.error(error);
        history.backgroundRefreshState.errorStates();
      } finally {
        history.backgroundRefreshState.finallyStates();
      }
    }),
    loadOlderMessages: flow(function* (chatId: number) {
      const history = self.getHistory(chatId);

      if (!history || !history.hasOlder || history.loadOlderState.pending) {
        return;
      }

      try {
        history.loadOlderState.loadingStates();

        if (history.hasNewer) {
          const response = yield HouseChats.GetHistory(
            {
              chat_id: chatId,
              limit: HISTORY_PAGE_SIZE,
              anchor_max_message_id: history.oldestMessage?.max_message_id,
            },
            self.sectionAborter.signal
          );

          history.appendOlderAnchored(response.items);
        } else {
          const response = yield HouseChats.GetHistory(
            {
              chat_id: chatId,
              limit: HISTORY_PAGE_SIZE,
              offset: history.messages.length,
            },
            self.sectionAborter.signal
          );

          history.appendOlderTip(response.items, response.total);
        }

        history.loadOlderState.successStates();
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        console.error(error);
        history.loadOlderState.errorStates();
      } finally {
        history.loadOlderState.finallyStates();
      }
    }),
    loadNewerMessages: flow(function* (chatId: number) {
      const history = self.getHistory(chatId);

      if (!history || !history.hasNewer || history.loadNewerState.pending) {
        return;
      }

      try {
        history.loadNewerState.loadingStates();

        const response = yield HouseChats.GetHistory(
          {
            chat_id: chatId,
            limit: HISTORY_PAGE_SIZE,
            anchor_max_message_id: history.newestMessage?.max_message_id,
          },
          self.sectionAborter.signal
        );

        history.appendNewerAnchored(response.items);
        self.syncSidebarPreview(chatId);
        history.loadNewerState.successStates();
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        console.error(error);
        history.loadNewerState.errorStates();
      } finally {
        history.loadNewerState.finallyStates();
      }
    }),
    jumpToMessage: flow(function* (chatId: number, maxMessageId: string) {
      // getOrCreateHistory, а не getHistory — переход может быть первым
      // открытием чата (клик по результату поиска), записи в
      // historyByChatId ещё нет.
      const history = self.getOrCreateHistory(chatId);

      if (history.hasMessage(maxMessageId)) {
        return true;
      }

      try {
        const response: HouseChats.HistoryResponseDTO =
          yield HouseChats.GetHistory(
            {
              chat_id: chatId,
              limit: HISTORY_PAGE_SIZE,
              anchor_max_message_id: maxMessageId,
            },
            self.sectionAborter.signal
          );

        const found = response.items.some(
          (item) => item.max_message_id === maxMessageId
        );

        if (!found) {
          return false;
        }

        history.jumpTo(response.items, response.total);

        return true;
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        console.error(error);

        return false;
      }
    }),
  }))
  .actions((self) => ({
    flushSystemEntities: flow(function* () {
      self.systemEntitiesFlushTimer = null;

      const queued = Array.from(self.queuedSystemEntityKeys);
      self.queuedSystemEntityKeys.clear();

      for (
        let offset = 0;
        offset < queued.length;
        offset += SYSTEM_ENTITIES_BATCH_SIZE
      ) {
        const keys = queued.slice(offset, offset + SYSTEM_ENTITIES_BATCH_SIZE);
        const entities = keys.map(
          (key) => self.requestedSystemEntities.get(key)!
        );
        const idsOf = (type: TSystemMessageEntity['type']) =>
          entities.filter((entity) => entity.type === type).map(({ id }) => id);

        try {
          const response = yield HouseChats.GetSystemEntitiesAvailability(
            { request_ids: idsOf('request'), appeal_ids: idsOf('appeal') },
            self.sectionAborter.signal
          );
          const available = new Set<string>([
            ...response.request_ids.map((id: string) =>
              systemEntityKey({ type: 'request', id })
            ),
            ...response.appeal_ids.map((id: string) =>
              systemEntityKey({ type: 'appeal', id })
            ),
          ]);

          keys.forEach((key) =>
            self.systemEntityAvailability.set(key, available.has(key))
          );
        } catch (error) {
          // без ответа ссылку не показываем (номер остаётся текстом), а
          // ключ отпускаем - следующий рендер спросит снова
          keys.forEach((key) => self.requestedSystemEntities.delete(key));

          if (isAbortError(error)) {
            // раздел сброшен - остальные пачки уже не нужны
            return;
          }

          console.error(error);
        }
      }
    }),
  }))
  .actions((self) => ({
    ensureSystemEntityAvailability(
      entity: Pick<TSystemMessageEntity, 'type' | 'id'>
    ) {
      const key = systemEntityKey(entity);

      if (self.requestedSystemEntities.has(key)) {
        return;
      }

      self.requestedSystemEntities.set(key, {
        type: entity.type,
        id: entity.id,
      });
      self.queuedSystemEntityKeys.add(key);

      // системные сообщения страницы истории рендерятся разом - собираем
      // их в один запрос
      if (!self.systemEntitiesFlushTimer) {
        self.systemEntitiesFlushTimer = setTimeout(
          () => self.flushSystemEntities(),
          SYSTEM_ENTITIES_FLUSH_DELAY_MS
        );
      }
    },
  }))
  .actions((self) => ({
    openChat(chatId: number) {
      const alreadyActive = self.activeChatId === chatId;

      if (self.activeChatId !== null && !alreadyActive) {
        self.getHistory(self.activeChatId)?.clearUnreadBoundary();
        self.abortChatRequests(self.activeChatId);
      }

      self.activeChatId = chatId;
      self.uiStates.setActiveChatAtBottom(true);
      self.touchHistoryAccess(chatId);
      self.loadParticipants(chatId);
      self.loadEmployees(chatId);

      const existing = self.getHistory(chatId);

      if (!existing) {
        self.loadLatest(chatId);

        return;
      }

      if (!alreadyActive) {
        // История уже закэширована с предыдущего визита — firstUnreadId
        // там мог остаться от старого unread_count (например, было 5
        // непрочитанных, часть прочитали в другом месте, осталось 1) —
        // пересчитываем границу под актуальный счётчик при каждом входе.
        const unread =
          self.chatsByChatId.get(String(chatId))?.unread_count ?? 0;

        existing.setUnreadBoundary(unread);
      }

      if (
        !alreadyActive &&
        !existing.hasNewer &&
        !existing.requestsState.pending &&
        !existing.backgroundRefreshState.pending
      ) {
        self.refreshLatestTail(chatId);
      }
    },
    // Отдельный сценарий открытия по клику на результат поиска сообщений —
    // openChat всегда грузит последнюю страницу (loadLatest), а тут заранее
    // известно, что нужна страница вокруг конкретного сообщения. Если
    // переиспользовать openChat + отдельный self.jumpToMessage поверх него,
    // сначала мелькает последняя страница (или пустой чат без
    // requestsState.pending — self.jumpToMessage его не проставляет), и
    // только потом — нужная. Здесь сразу грузим то, что нужно, с корректным
    // pending, чтобы ConnectedChat показал скелетон, а не пустой чат.
    openChatAtMessage: flow(function* (chatId: number, maxMessageId: string) {
      const alreadyActive = self.activeChatId === chatId;

      if (self.activeChatId !== null && !alreadyActive) {
        self.getHistory(self.activeChatId)?.clearUnreadBoundary();
        self.abortChatRequests(self.activeChatId);
      }

      self.selectedId = `chat-${chatId}`;
      self.activeChatId = chatId;
      self.uiStates.setActiveChatAtBottom(false);
      self.touchHistoryAccess(chatId);
      self.loadParticipants(chatId);
      self.loadEmployees(chatId);

      const history = self.getOrCreateHistory(chatId);

      if (history.hasMessage(maxMessageId)) {
        return;
      }

      try {
        history.requestsState.loadingStates();

        const response = yield HouseChats.GetHistory(
          {
            chat_id: chatId,
            limit: HISTORY_PAGE_SIZE,
            anchor_max_message_id: maxMessageId,
          },
          self.sectionAborter.signal
        );

        const found = response.items.some(
          (item: { max_message_id: string }) =>
            item.max_message_id === maxMessageId
        );

        if (found) {
          history.jumpTo(response.items, response.total);
          history.requestsState.successStates();
        } else {
          history.requestsState.errorStates();
        }
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        console.error(error);
        history.requestsState.errorStates();
      } finally {
        history.requestsState.finallyStates();
      }
    }),
    deliverMessage: flow(function* (chatId: number, tempId: string) {
      const history = self.getHistory(chatId);
      const message = history?.findById(tempId);

      if (!history || !message) {
        return;
      }

      message.setSendStatus('sending');

      try {
        const files = self.pendingFilesByTempId.get(tempId) ?? [];
        const uploaded: { file_id: string; filename: string }[] =
          self.uploadedFilesByTempId.get(tempId) ??
          (yield Promise.all(files.map(uploadAttachmentFile)));

        self.uploadedFilesByTempId.set(tempId, uploaded);

        const response = yield HouseChats.ForwardMessage({
          text: message.text ?? undefined,
          target_chat_ids: [chatId],
          is_massive: false,
          reply_to_max_id: message.reply_to_max_id ?? undefined,
          attachment_file_ids: uploaded.length
            ? uploaded.map((a) => a.file_id)
            : undefined,
          attachment_filenames: uploaded.length
            ? uploaded.map((a) => a.filename)
            : undefined,
        });

        const realId = response.sent_message_ids?.[0];

        if (realId) {
          history.confirmOptimistic(tempId, realId);
          self.pendingFilesByTempId.delete(tempId);
          self.uploadedFilesByTempId.delete(tempId);
          self.syncSidebarPreview(chatId);
          self.refreshChatStatsIfOpen(chatId);
        } else if (isAlive(message)) {
          message.setSendStatus('error');
        }
      } catch (error) {
        console.error(error);

        if (isAlive(message)) {
          message.setSendStatus('error');
        }
      }
    }),
    sendMessage(
      chatId: number,
      text: string,
      reply?: {
        maxMessageId: string;
        senderName: string;
        preview: string;
      } | null,
      files: File[] = []
    ) {
      const lastMessageId =
        self.getHistory(chatId)?.newestMessage?.max_message_id;

      if (lastMessageId) {
        self.markChatRead(chatId, lastMessageId);
      }

      const mediaFiles = files.filter((file) => {
        const type = categorizeFileType(file);

        return type === 'image' || type === 'video';
      });
      const audioFiles = files.filter(
        (file) => categorizeFileType(file) === 'audio'
      );
      const plainFiles = files.filter(
        (file) => categorizeFileType(file) === 'file'
      );

      const groups = [
        mediaFiles,
        audioFiles,
        ...plainFiles.map((file) => [file]),
      ].filter((group) => group.length);

      if (!groups.length) {
        this.sendSingleMessage(chatId, text, reply, files);

        return;
      }

      const tempIds = groups.map((group, index) =>
        this.stageOptimisticMessage(
          chatId,
          index === 0 ? text : '',
          index === 0 ? reply : null,
          group
        )
      );

      tempIds.reduce<Promise<unknown>>(
        (promise, tempId) =>
          tempId
            ? promise.then(() => this.deliverMessage(chatId, tempId))
            : promise,
        Promise.resolve()
      );
    },
    stageOptimisticMessage(
      chatId: number,
      text: string,
      reply:
        | {
            maxMessageId: string;
            senderName: string;
            preview: string;
          }
        | null
        | undefined,
      files: File[]
    ): string | null {
      const history = self.getHistory(chatId);

      if (!history) {
        return null;
      }

      const tempId = `pending-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;

      const attachments = files.map((file, index) => ({
        id: `${tempId}-${index}`,
        type: categorizeFileType(file),
        url: URL.createObjectURL(file),
        filename: file.name,
        size: file.size,
        thumbnailUrl: null,
        duration: null,
      }));

      history.addOptimistic({
        id: tempId,
        max_message_id: tempId,
        text,
        created_at: new Date().toISOString(),
        system_sender: { first_name: null, last_name: null, is_bot: false },
        has_attachments: !!attachments.length,
        attachments,
        reply_to_max_id: reply?.maxMessageId ?? null,
        reply_preview: reply
          ? { sender_name: reply.senderName, preview: reply.preview }
          : null,
        sendStatus: 'sending',
      });

      history.findById(tempId)?.flagJustAdded();

      if (files.length) {
        self.pendingFilesByTempId.set(tempId, files);
      }

      return tempId;
    },
    sendSingleMessage(
      chatId: number,
      text: string,
      reply:
        | {
            maxMessageId: string;
            senderName: string;
            preview: string;
          }
        | null
        | undefined,
      files: File[]
    ) {
      const tempId = this.stageOptimisticMessage(chatId, text, reply, files);

      if (!tempId) {
        return;
      }

      return this.deliverMessage(chatId, tempId);
    },
    retrySend(chatId: number, tempId: string) {
      this.deliverMessage(chatId, tempId);
    },
    cancelSend(chatId: number, tempId: string) {
      self.getHistory(chatId)?.removeById(tempId);
      self.pendingFilesByTempId.delete(tempId);
      self.uploadedFilesByTempId.delete(tempId);
    },
    deliverMassMessage: flow(function* (tempId: string) {
      const history = self.massMailingHistory;
      const optimisticMessage = history.findById(tempId);

      if (!optimisticMessage) {
        return;
      }

      optimisticMessage.setSendStatus('sending');

      try {
        const files = self.pendingFilesByTempId.get(tempId) ?? [];
        const uploaded: { file_id: string; filename: string }[] =
          self.uploadedFilesByTempId.get(tempId) ??
          (yield Promise.all(files.map(uploadAttachmentFile)));

        self.uploadedFilesByTempId.set(tempId, uploaded);

        yield HouseChats.ForwardMessage({
          text: optimisticMessage.text ?? undefined,
          target_chat_ids: null,
          is_massive: true,
          attachment_file_ids: uploaded.length
            ? uploaded.map((a) => a.file_id)
            : undefined,
          attachment_filenames: uploaded.length
            ? uploaded.map((a) => a.filename)
            : undefined,
        });

        // У рассылки бэк не возвращает id сообщений — подтверждать нечем,
        // успех это просто отсутствие ошибки. Оптимистичное сообщение живёт
        // с временным id до перезагрузки истории рассылок.
        self.pendingFilesByTempId.delete(tempId);
        self.uploadedFilesByTempId.delete(tempId);

        if (isAlive(optimisticMessage)) {
          optimisticMessage.setSendStatus(null);
        }

        message({ type: 'success', text: 'Сообщения отправлены' });
      } catch (error) {
        console.error(error);

        if (isAlive(optimisticMessage)) {
          optimisticMessage.setSendStatus('error');
        }
      }
    }),
    stageMassMailingMessage(text: string, files: File[]) {
      const id = `staged-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      self.massMailingStaged = [{ id, text, files }, ...self.massMailingStaged];
    },
    unstageMassMailingMessage(id: string) {
      self.massMailingStaged = self.massMailingStaged.filter(
        (staged) => staged.id !== id
      );
    },
    sendMassMessage(text: string, files: File[] = []) {
      const history = self.massMailingHistory;

      const tempId = `pending-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;

      const attachments = files.map((file, index) => ({
        id: `${tempId}-${index}`,
        type: categorizeFileType(file),
        url: URL.createObjectURL(file),
        filename: file.name,
        size: file.size,
        thumbnailUrl: null,
        duration: null,
      }));

      history.addOptimistic({
        id: tempId,
        max_message_id: tempId,
        text,
        created_at: new Date().toISOString(),
        system_sender: { first_name: null, last_name: null, is_bot: false },
        has_attachments: !!attachments.length,
        attachments,
        is_massive: true,
        reply_to_max_id: null,
        reply_preview: null,
        sendStatus: 'sending',
      });
      history.findById(tempId)?.flagJustAdded();

      if (files.length) {
        self.pendingFilesByTempId.set(tempId, files);
      }

      this.deliverMassMessage(tempId);
    },
    retryMassSend(tempId: string) {
      this.deliverMassMessage(tempId);
    },
    cancelMassSend(tempId: string) {
      self.massMailingHistory.removeById(tempId);
      self.pendingFilesByTempId.delete(tempId);
      self.uploadedFilesByTempId.delete(tempId);
    },
    editMessage: flow(function* (
      chatId: number,
      maxMessageId: string,
      text: string
    ) {
      const message = self.getHistory(chatId)?.findMessage(maxMessageId);

      if (!message) {
        return;
      }

      try {
        yield HouseChats.ChangeMessage({
          chat_id: chatId,
          max_message_id: maxMessageId,
          text,
        });

        message.editText(text);
        self.syncSidebarPreview(chatId);
      } catch (error) {
        console.error(error);

        return Promise.reject(error);
      }
    }),
    deleteMessage: flow(function* (chatId: number, maxMessageId: string) {
      const message = self.getHistory(chatId)?.findMessage(maxMessageId);

      if (!message) {
        return;
      }

      try {
        yield HouseChats.DeleteMessage({
          chat_id: chatId,
          max_message_id: maxMessageId,
        });

        message.markDeleted();
        self.syncSidebarPreview(chatId);
      } catch (error) {
        console.error(error);

        return Promise.reject(error);
      }
    }),
    retryLoadHistory(chatId: number) {
      self.loadLatest(chatId);
    },
    loadMassMailingLatest: flow(function* () {
      const history = self.massMailingHistory;

      try {
        history.requestsState.loadingStates();

        const response = yield HouseChats.GetMassiveMessages(
          {
            limit: HISTORY_PAGE_SIZE,
          },
          self.sectionAborter.signal
        );

        history.setLatest(
          response.items.map(normalizeMassiveMessage),
          response.total
        );
        history.requestsState.successStates();
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        console.error(error);
        history.requestsState.errorStates();
      } finally {
        history.requestsState.finallyStates();
      }
    }),
    loadOlderMassMailingMessages: flow(function* () {
      const history = self.massMailingHistory;

      if (!history.hasOlder || history.loadOlderState.pending) {
        return;
      }

      try {
        history.loadOlderState.loadingStates();

        const response = yield HouseChats.GetMassiveMessages(
          {
            limit: HISTORY_PAGE_SIZE,
            offset: history.messages.length,
          },
          self.sectionAborter.signal
        );

        history.appendOlderTip(
          response.items.map(normalizeMassiveMessage),
          response.total
        );
        history.loadOlderState.successStates();
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        console.error(error);
        history.loadOlderState.errorStates();
      } finally {
        history.loadOlderState.finallyStates();
      }
    }),
    retryLoadMassMailingHistory() {
      this.loadMassMailingLatest();
    },
    openMassMailing() {
      if (self.activeChatId !== null) {
        self.getHistory(self.activeChatId)?.clearUnreadBoundary();
        self.abortChatRequests(self.activeChatId);
        self.activeChatId = null;
      }

      self.selectedId = 'mass-mailing';

      if (self.massMailingHistory.requestsState.readyToLoad) {
        this.loadMassMailingLatest();
      }
    },
    selectRow(id: string | null) {
      self.selectedId = id;

      const chatId = parseChatId(id);

      if (chatId !== null) {
        this.openChat(chatId);
      } else {
        if (self.activeChatId !== null) {
          self.getHistory(self.activeChatId)?.clearUnreadBoundary();
          self.abortChatRequests(self.activeChatId);
        }

        self.activeChatId = null;
      }
    },
    // Смена ссылки: бэк помечает старую привязку disconnected и заводит по
    // дому новую. Пользователь смотрит уже отключённый чат — переводим его
    // на свежую запись, иначе он остаётся в мёртвом.
    reselectHouseChat(houseIds: string[]) {
      const currentKey = self.selectedId?.startsWith('chat-')
        ? self.selectedId.slice('chat-'.length)
        : null;
      const current = currentKey
        ? self.chatsByChatId.get(currentKey)
        : undefined;

      if (current?.status !== 'disconnected') {
        return;
      }

      const houseId = current.house_ids.find((id) => houseIds.includes(id));

      if (!houseId) {
        return;
      }

      let replacement: TChatItem | undefined;

      self.chatsByChatId.forEach((chat) => {
        if (
          chat !== current &&
          chat.status !== 'disconnected' &&
          chat.house_ids.includes(houseId)
        ) {
          replacement = chat;
        }
      });

      if (replacement) {
        this.selectRow(`chat-${chatMapKey(replacement)}`);
      }
    },
    refreshAttachmentsTabs(
      chatId: number,
      attachments: MaxMessageCreatedPayload['attachments']
    ) {
      collectAttachmentTabTypes(attachments).forEach((type) => {
        if (self.attachmentsByTabKey.has(attachmentsTabKey(chatId, type))) {
          self.retryAttachmentsTab(chatId, type);
        }
      });
    },
    pullChatIntoList: flow(function* (chatId: number, houseIds: string[]) {
      if (self.pullingChatIds.has(chatId)) {
        return;
      }

      self.pullingChatIds.add(chatId);

      try {
        const response = yield HouseChats.GetChats(
          {
            house_ids: houseIds,
            limit: houseIds.length,
            offset: 0,
            ...buildChatsFilterParams(self),
          },
          self.sectionAborter.signal
        );

        response.items.forEach((item: ChatItemDTO) => {
          self.chatsByChatId.set(chatMapKey(item), item);
        });
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        console.error(error);
      } finally {
        self.pullingChatIds.delete(chatId);
      }
    }),
    applyRealtimeMessage(payload: MaxMessageCreatedPayload) {
      const { chat_id: chatId } = payload;

      if (typeof chatId !== 'number') {
        return;
      }

      const message = normalizeMaxMessage(payload);
      const history = self.getHistory(chatId);

      history?.prependNewer(message);

      if (
        history &&
        self.activeChatId === chatId &&
        history.newestMessage?.max_message_id === payload.max_message_id
      ) {
        history.newestMessage.flagJustAdded();
      }

      const chatItem = self.chatsByChatId.get(String(chatId));

      chatItem?.setLastMessage(message);

      const isOwnMessage = isCurrentUserSender(
        payload.system_sender,
        self.currentUserNumber
      );

      if (self.activeChatId === chatId && self.uiStates.activeChatAtBottom) {
        self.markChatRead(chatId, payload.max_message_id);
      } else if (chatItem && !isOwnMessage) {
        chatItem.incrementUnread();
      } else if (!chatItem && !isOwnMessage && payload.house_ids.length) {
        this.pullChatIntoList(chatId, payload.house_ids);
      }

      this.refreshAttachmentsTabs(chatId, payload.attachments);
      self.refreshChatStatsIfOpen(chatId);
    },
    applyRealtimeEdit(payload: MaxMessageEditedPayload) {
      const { chat_id: chatId, max_message_id: maxMessageId } = payload;

      if (typeof chatId !== 'number') {
        return;
      }

      const history = self.getHistory(chatId);
      const message = history?.findMessage(maxMessageId);

      if (!history || !message) {
        return;
      }

      message.applyEdit(
        payload.text ?? null,
        normalizeAttachments(payload.attachments)
      );

      this.refreshAttachmentsTabs(chatId, payload.attachments);

      if (!history.hasNewer && history.newestMessage === message) {
        self.chatsByChatId
          .get(String(chatId))
          ?.setLastMessage(getSnapshot(message));
      }
    },
    applyRealtimeRemove(payload: MaxMessageRemovedPayload) {
      const { chat_id: chatId, max_message_id: maxMessageId } = payload;

      if (typeof chatId !== 'number') {
        return;
      }

      const history = self.getHistory(chatId);
      const message = history?.findMessage(maxMessageId);

      if (!history || !message) {
        return;
      }

      message.markDeleted();

      if (!history.hasNewer && history.newestMessage === message) {
        self.chatsByChatId
          .get(String(chatId))
          ?.setLastMessage(getSnapshot(message));
      }
    },
    applyBotRemoved(payload: MaxBotRemovedPayload) {
      self.chatsByChatId
        .get(String(payload.chat_id))
        ?.setStatus('disconnected');
    },
    applyUserAdded(payload: MaxUserAddedPayload) {
      self.participantsByChatId
        .get(String(payload.chat_id))
        ?.addParticipant(payload.sender);
    },
    applyUserRemoved(payload: MaxUserRemovedPayload) {
      self.participantsByChatId
        .get(String(payload.chat_id))
        ?.removeParticipant(payload.user_id);
    },
    setSearchQuery(value: string) {
      self.searchQuery = value;

      if (value.trim() && self.selectedId === 'mass-mailing') {
        self.selectedId = null;
      }
    },
    setStatusFilter(value: HouseChatsStatusFilter) {
      self.statusFilter = value;

      if (value !== 'all' && self.selectedId === 'mass-mailing') {
        self.selectedId = null;
      }

      this.getChats();
    },
    setUnreadOnly(value: boolean) {
      self.unreadOnly = value;

      if (value && self.selectedId === 'mass-mailing') {
        self.selectedId = null;
      }

      this.getChats();
    },
    getChats: flow(function* () {
      try {
        self.requestsStates.getChats.loadingStates();

        const response = yield HouseChats.GetChats(
          {
            limit: CHATS_PAGE_SIZE,
            offset: 0,
            ...buildChatsFilterParams(self),
          },
          self.sectionAborter.signal
        );

        self.chatsByChatId.clear();
        response.items.forEach((item: ChatItemDTO) => {
          self.chatsByChatId.set(chatMapKey(item), item);
        });
        self.chatsOffset = response.items.length;
        self.chatsTotal = response.total;

        self.requestsStates.getChats.successStates();
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        console.error(error);
        self.requestsStates.getChats.errorStates();
      } finally {
        self.requestsStates.getChats.finallyStates();
      }
    }),
    loadMoreChats: flow(function* () {
      if (
        self.requestsStates.getChats.pending ||
        self.requestsStates.loadMoreChats.pending ||
        !self.hasMoreChats
      ) {
        return;
      }

      try {
        self.requestsStates.loadMoreChats.loadingStates();

        const response = yield HouseChats.GetChats(
          {
            limit: CHATS_PAGE_SIZE,
            offset: self.chatsOffset,
            ...buildChatsFilterParams(self),
          },
          self.sectionAborter.signal
        );

        response.items.forEach((item: ChatItemDTO) => {
          self.chatsByChatId.set(chatMapKey(item), item);
        });
        self.chatsOffset += response.items.length;
        self.chatsTotal = response.total;

        self.requestsStates.loadMoreChats.successStates();
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        console.error(error);
        self.requestsStates.loadMoreChats.errorStates();
      } finally {
        self.requestsStates.loadMoreChats.finallyStates();
      }
    }),
    // Поиск по сообщениям (а не по чатам) — отдельный эндпоинт, плоский
    // список совпадений, один чат может встретиться несколько раз.
    searchMessages: flow(function* (query: string) {
      self.searchAborter?.abort();
      const aborter = new AbortController();
      self.searchAborter = aborter;

      try {
        self.requestsStates.searchMessages.loadingStates();

        const response = yield HouseChats.SearchMessages(
          {
            query,
            limit: CHATS_PAGE_SIZE,
            offset: 0,
          },
          aborter.signal
        );

        self.messageSearchResults = response.items;
        self.addressMatchChats = cast(response.address_matches ?? []);
        self.addressMatchesTruncated = !!response.address_matches_truncated;
        self.messageSearchOffset = response.items.length;
        self.messageSearchTotal = response.total;

        self.requestsStates.searchMessages.successStates();
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        console.error(error);
        self.requestsStates.searchMessages.errorStates();
      } finally {
        self.requestsStates.searchMessages.finallyStates();
      }
    }),
    loadMoreMessageSearchResults: flow(function* (query: string) {
      if (
        self.requestsStates.searchMessages.pending ||
        self.requestsStates.loadMoreSearchMessages.pending ||
        !self.hasMoreMessageSearchResults
      ) {
        return;
      }

      try {
        self.requestsStates.loadMoreSearchMessages.loadingStates();

        const response = yield HouseChats.SearchMessages({
          query,
          limit: CHATS_PAGE_SIZE,
          offset: self.messageSearchOffset,
        });

        if (self.searchQuery.trim() !== query) {
          return;
        }

        self.messageSearchResults = [
          ...self.messageSearchResults,
          ...response.items,
        ];
        self.messageSearchOffset += response.items.length;
        self.messageSearchTotal = response.total;

        self.requestsStates.loadMoreSearchMessages.successStates();
      } catch (error) {
        console.error(error);
        self.requestsStates.loadMoreSearchMessages.errorStates();
      } finally {
        self.requestsStates.loadMoreSearchMessages.finallyStates();
      }
    }),
    clearMessageSearchResults() {
      self.searchAborter?.abort();
      self.searchAborter = null;
      self.messageSearchResults = [];
      self.addressMatchChats = cast([]);
      self.addressMatchesTruncated = false;
      self.messageSearchOffset = 0;
      self.messageSearchTotal = null;
    },
    // Уход из раздела: blob-миниатюры иначе живут до перезагрузки вкладки, а
    // история с чатами всё равно протухнут, пока работают в других разделах.
    // Черновики (draftsByChatId) и подготовленные рассылки (volatile
    // massMailingStaged) переживают сброс — это набранное руками, не кэш.
    resetSection() {
      self.searchAborter?.abort();
      self.searchAborter = null;

      // чтения раздела (чаты, история, рассылки, участники, статистика) —
      // одним сигналом, иначе ответ по дороге наполнил бы очищенный стор
      self.sectionAborter.abort();
      self.sectionAborter = new AbortController();

      [self.attachmentAborters, self.tabAborters].forEach((aborters) => {
        aborters.forEach((controller) => controller.abort());
        aborters.clear();
      });

      self.attachmentCache.forEach((entry) => {
        if (entry.thumbUrl && entry.thumbUrl !== entry.url) {
          URL.revokeObjectURL(entry.thumbUrl);
        }
      });

      applySnapshot(self, {
        draftsByChatId: getSnapshot(self.draftsByChatId),
      });

      self.attachmentQueue = [];
      self.pendingFilesByTempId.clear();
      self.uploadedFilesByTempId.clear();
      self.pullingChatIds.clear();
      self.refreshingHouseIds.clear();
      self.messageSearchResults = [];
      self.messageSearchOffset = 0;
      self.messageSearchTotal = null;
      self.addressMatchesTruncated = false;
      self.chatsOffset = 0;
      self.chatsTotal = null;

      // при возврате в раздел доступ перепроверяется: заявку могли удалить,
      // а права сотрудника - поменять
      if (self.systemEntitiesFlushTimer) {
        clearTimeout(self.systemEntitiesFlushTimer);
        self.systemEntitiesFlushTimer = null;
      }

      self.systemEntityAvailability.clear();
      self.requestedSystemEntities.clear();
      self.queuedSystemEntityKeys.clear();
    },
    checkUnreadChats: flow(function* () {
      if (self.unreadCheckPending) {
        return;
      }

      self.unreadCheckPending = true;

      try {
        const response = yield HouseChats.GetChats({
          limit: 1,
          offset: 0,
          unread_only: true,
        });

        self.hasUnreadChats = response.total > 0;
      } catch (error) {
        console.error(error);
      } finally {
        self.unreadCheckPending = false;
      }
    }),
    // Точечное обновление одного/нескольких чатов по домам — используется
    // после мутаций (бот добавлен, ссылку на чат поменяли), чтобы не сбрасывать
    // уже подгруженные страницы сайдбара полным рефетчем.
    refreshChatsByHouseIds: flow(function* (houseIds: string[]) {
      try {
        const response = yield HouseChats.GetChats(
          {
            house_ids: houseIds,
            // на дом может приходиться больше одной записи: после смены ссылки
            // старая привязка остаётся рядом с новой как disconnected. Урезать
            // выдачу нельзя — всё, что не пришло, отсюда же и удаляется.
            limit: Math.max(houseIds.length * 2, CHATS_PAGE_SIZE),
            offset: 0,
          },
          self.sectionAborter.signal
        );

        // Пока бот не добавлен, запись живёт под pending-ключом (см.
        // chatMapKey) — как только chat_id появляется, у той же записи
        // меняется ключ, и старую pending-запись по этим домам нужно убрать
        // явно, иначе она зависнет в мапе дублем.
        const freshKeys = new Set(response.items.map(chatMapKey));
        const selectedKey = self.selectedId?.startsWith('chat-')
          ? self.selectedId.slice('chat-'.length)
          : null;
        let selectedStaleHouseIds: string[] | null = null;

        self.chatsByChatId.forEach((chat, key) => {
          const overlaps = chat.house_ids.some((id) => houseIds.includes(id));
          const stringKey = String(key);

          if (overlaps && !freshKeys.has(stringKey)) {
            if (stringKey === selectedKey) {
              selectedStaleHouseIds = [...chat.house_ids];
            }

            self.chatsByChatId.delete(stringKey);
          }
        });

        response.items.forEach((item: ChatItemDTO) => {
          self.chatsByChatId.set(chatMapKey(item), item);
        });

        // Выбранная pending-запись получила chat_id и сменила ключ — без
        // перевыбора (его делает вызывающий) выбор повисает на удалённой записи
        if (selectedStaleHouseIds) {
          const staleHouseIds: string[] = selectedStaleHouseIds;
          const replacement = response.items.find(
            (item: ChatItemDTO) =>
              item.status !== 'disconnected' &&
              item.house_ids.some((id) => staleHouseIds.includes(id))
          );

          if (replacement) {
            return `chat-${chatMapKey(replacement)}`;
          }
        }

        return null;
      } catch (error) {
        if (isAbortError(error)) {
          return null;
        }

        console.error(error);

        return null;
      }
    }),
  }))
  .actions((self) => ({
    loadInitialData: flow(function* () {
      if (!self.canLoadInitialData) {
        return;
      }

      self.initialLoadAttempts += 1;

      yield self.getChats();
    }),
    retryInitialLoad() {
      self.initialLoadAttempts = 0;
      self.requestsStates.getChats.clearStates();
    },
    applyBotAdded: flow(function* (payload: MaxBotAddedPayload) {
      const replacementRowId: string | null = yield self.refreshChatsByHouseIds(
        payload.house_ids
      );

      // Подтверждение по этим домам получено — снимаем "обновляется",
      // независимо от того, что именно вызвало событие (connectChatByLink
      // или что-то ещё, например действие другого сотрудника).
      payload.house_ids.forEach((houseId) => {
        self.refreshingHouseIds.delete(houseId);
      });

      if (replacementRowId) {
        self.selectRow(replacementRowId);
      }

      self.reselectHouseChat(payload.house_ids);

      // /chats отдаёт только last_message для превью в сайдбаре — если чат
      // уже открыт (история закэширована), подтягиваем и сам хвост сообщений.
      // chat_id === null — событие пришло из-за отвязки ссылки, реального
      // чата ещё/уже нет, подтягивать нечего.
      if (payload.chat_id !== null) {
        yield self.refreshLatestTail(payload.chat_id);
      }
    }),
    connectChatByLink: flow(function* (
      house_id: string,
      max_chat_url: string,
      hadLink: boolean
    ) {
      try {
        if (!max_chat_url) {
          yield HouseChats.DeleteChatLink(house_id);
        } else if (hadLink) {
          yield HouseChats.DeleteChatLink(house_id);
          yield HouseChats.MapChat({ house_id, max_chat_url });
        } else {
          yield HouseChats.MapChat({ house_id, max_chat_url });
        }

        // Мутация принята, но реальный статус чата бэк досчитывает
        // асинхронно — до подтверждающего max_bot_added (см. applyBotAdded)
        // держим строку в состоянии "обновляется", а не рисуем старый
        // connected/needsChatLink из ещё не обновлённого chatsByChatId.
        // ponytail: если сокет отвалится и событие потеряется, флаг тут
        // зависнет навсегда — апгрейд: сбрасывать refreshingHouseIds по
        // переподключению сокета или по TTL, если это станет реальной проблемой.
        self.refreshingHouseIds.add(house_id);

        // Новая привязка появляется сразу по ответу на POST, не дожидаясь
        // max_bot_added (его вовсе не будет, пока бота не добавят в новый
        // чат) — подтягиваем её и переводим выделение
        if (max_chat_url) {
          const replacementRowId: string | null =
            yield self.refreshChatsByHouseIds([house_id]);

          if (replacementRowId) {
            self.selectRow(replacementRowId);
          }

          self.reselectHouseChat([house_id]);

          // max_bot_added не придёт, пока бота не добавят — ждать нечего,
          // иначе строка (и InfoColumn) зависнет в "обновляется"
          self.refreshingHouseIds.delete(house_id);
        }

        return 'ok' as const;
      } catch (error) {
        console.error(error);

        return 'error' as const;
      }
    }),
  }));
